import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Hash } from 'lucide-react';
import { usePosts } from '../contexts/PostContext';
import PostCard from '../components/posts/PostCard';
import { supabase } from '../lib/supabase';

interface TopicStats {
  post_count: number;
  engagement_score: number;
}

const TopicPage: React.FC = () => {
  const { topic } = useParams<{ topic: string }>();
  const { posts } = usePosts();
  const [topicStats, setTopicStats] = useState<TopicStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTopicStats = async () => {
      if (!topic) return;

      try {
        const { data, error } = await supabase
          .from('trending_topics')
          .select('post_count, engagement_score')
          .eq('topic', topic)
          .maybeSingle();

        if (error) throw error;
        setTopicStats(data);
      } catch (error) {
        console.error('Error fetching topic stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopicStats();
  }, [topic]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  const topicPosts = posts.filter(post => 
    post.topic?.toLowerCase() === topic?.toLowerCase()
  ).sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div>
      <div className="mb-8">
        <Link 
          to="/home"
          className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Hash className="h-8 w-8 text-purple-400 mr-3" />
            <h1 className="text-2xl font-bold text-white capitalize">
              {topic}
            </h1>
          </div>
          {topicStats && (
            <div className="text-gray-400 text-sm">
              {topicStats.post_count} {topicStats.post_count === 1 ? 'post' : 'posts'}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {topicPosts.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400 mb-4">No posts in this topic yet</p>
            <Link 
              to="/home/create" 
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              Create the first post
            </Link>
          </div>
        ) : (
          topicPosts.map(post => (
            <PostCard key={post.id} post={post} />
          ))
        )}
      </div>
    </div>
  );
};

export default TopicPage;